import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Badge } from '@/components/ui/badge';
import { CheckCircle } from 'lucide-react';

interface InfiniteScrollTagsProps {
  items: Array<{ id: string; label: string }>;
  selectedItems: string[];
  onItemToggle: (id: string) => void;
  direction?: 'left' | 'right';
}

const InfiniteScrollTags: React.FC<InfiniteScrollTagsProps> = ({
  items,
  selectedItems,
  onItemToggle,
  direction = 'left'
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [clickStartTime, setClickStartTime] = useState(0);
  const [clickStartPosition, setClickStartPosition] = useState({ x: 0, y: 0 });
  
  // Animation state refs (to avoid re-renders during animation)
  const animationRef = useRef<number | null>(null);
  const scrollPositionRef = useRef(0);
  const scrollSpeedRef = useRef(0.3); // base speed
  const wasManuallyScrolledRef = useRef(false);
  const lastScrollTimeRef = useRef(0);
  const transitioningRef = useRef(false);
  const resumeDelayTimerRef = useRef<number | null>(null);
  
  // Clone content for seamless scrolling
  const cloneContent = useCallback(() => {
    if (!contentRef.current || !scrollContainerRef.current) return;
    
    // Reset the container to its original state
    if (contentRef.current.children.length > items.length) {
      while (contentRef.current.children.length > items.length) {
        contentRef.current.removeChild(contentRef.current.lastChild as Node);
      }
    }
    
    // Clone items for infinite scroll effect
    const itemElements = Array.from(contentRef.current.children);
    itemElements.forEach(item => {
      const clone = item.cloneNode(true) as HTMLElement;
      contentRef.current?.appendChild(clone);
    });
    
    // Initialize scroll position only once on first render
    if (scrollContainerRef.current.scrollLeft === 0 && scrollPositionRef.current === 0) {
      scrollPositionRef.current = 0;
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollLeft = 0;
      }
    }
  }, [items.length]);
  
  // Smoothly transition from manual to auto scroll
  const syncScrollPosition = useCallback(() => {
    if (!scrollContainerRef.current) return;
    
    // Update our reference to current actual scroll position
    scrollPositionRef.current = scrollContainerRef.current.scrollLeft;
    
    // Mark that we're handling the transition properly
    transitioningRef.current = false;
    wasManuallyScrolledRef.current = false;
  }, []);
  
  // Animation function using requestAnimationFrame with damping
  const animateScroll = useCallback(() => {
    if (!scrollContainerRef.current || isPaused) {
      return;
    }
    
    const container = scrollContainerRef.current;
    const content = contentRef.current;
    if (!content) return;
    
    // If we just came from manual scrolling, sync before continuing
    if (wasManuallyScrolledRef.current) {
      syncScrollPosition();
    }
    
    // Update scroll position
    const delta = direction === 'right' ? -scrollSpeedRef.current : scrollSpeedRef.current;
    scrollPositionRef.current += delta;
    
    // Apply scroll position to container
    container.scrollLeft = scrollPositionRef.current;
    
    // Reset scroll when we've scrolled through half the content (seamless loop)
    const halfContentWidth = content.scrollWidth / 2;
    if (container.scrollLeft >= halfContentWidth) {
      // Use a graduated approach to avoid visual jumps
      scrollPositionRef.current = 0;
      container.scrollLeft = 0;
    }
    if (container.scrollLeft <= 0 && direction === 'right') {
      // when moving rightwards (negative direction), loop from half
      scrollPositionRef.current = halfContentWidth;
      container.scrollLeft = halfContentWidth;
    }
    
    // Record last animation time
    lastScrollTimeRef.current = Date.now();
    
    // Continue animation
    animationRef.current = requestAnimationFrame(animateScroll);
  }, [isPaused, syncScrollPosition]);
  
  // Start the animation initially and handle cleanup
  useEffect(() => {
    cloneContent();
    
    // Wait a bit for the DOM to settle before starting animation
    const timer = setTimeout(() => {
      if (!isPaused && !animationRef.current) {
        animationRef.current = requestAnimationFrame(animateScroll);
      }
    }, 100);
    
    return () => {
      if (timer) clearTimeout(timer);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      if (resumeDelayTimerRef.current) {
        clearTimeout(resumeDelayTimerRef.current);
        resumeDelayTimerRef.current = null;
      }
    };
  }, [cloneContent, animateScroll, isPaused]);
  
  // Handle pausing animation on hover
  const handleMouseEnter = () => {
    setIsPaused(true);
    
    if (resumeDelayTimerRef.current) {
      clearTimeout(resumeDelayTimerRef.current);
      resumeDelayTimerRef.current = null;
    }
    
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    
    // On mouse enter, make sure we capture the current container position
    if (scrollContainerRef.current) {
      scrollPositionRef.current = scrollContainerRef.current.scrollLeft;
    }
  };
  
  // Resume animation when mouse leaves - with sync to current position
  const handleMouseLeave = () => {
    // Only resume if not currently dragging
    if (!isDragging) {
      // Always sync the position ref with the actual scroll position first
      if (scrollContainerRef.current) {
        scrollPositionRef.current = scrollContainerRef.current.scrollLeft;
      }
      
      // Clear any existing delayed resume
      if (resumeDelayTimerRef.current) {
        clearTimeout(resumeDelayTimerRef.current);
      }
      
      // Add a small delay before resuming to ensure smooth transition
      resumeDelayTimerRef.current = window.setTimeout(() => {
        setIsPaused(false);
        wasManuallyScrolledRef.current = false;
        if (!animationRef.current) {
          animationRef.current = requestAnimationFrame(animateScroll);
        }
        resumeDelayTimerRef.current = null;
      }, 150);
    }
  };
  
  // Handle drag interaction for manual scrolling
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollContainerRef.current) return;
    
    // Stop any pending resume timer
    if (resumeDelayTimerRef.current) {
      clearTimeout(resumeDelayTimerRef.current);
      resumeDelayTimerRef.current = null;
    }
    
    setIsDragging(true);
    setIsPaused(true);
    setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
    
    // Track for click detection
    setClickStartTime(Date.now());
    setClickStartPosition({ x: e.pageX, y: e.pageY });
    
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    
    // Mark that we're starting manual interaction
    wasManuallyScrolledRef.current = true;
    transitioningRef.current = true;
  };
  
  const handleMouseUp = (e: React.MouseEvent, id: string) => {
    if (!isDragging) return;
    
    setIsDragging(false);
    
    // Always mark that we've manually scrolled
    wasManuallyScrolledRef.current = true;
    
    // Update scrollPositionRef to match the current container position
    if (scrollContainerRef.current) {
      scrollPositionRef.current = scrollContainerRef.current.scrollLeft;
    }
    
    // Detect if this was a click (not a drag)
    const isClick = 
      Date.now() - clickStartTime < 200 && // Short duration
      Math.abs(e.pageX - clickStartPosition.x) < 10 && // Small X movement
      Math.abs(e.pageY - clickStartPosition.y) < 10;   // Small Y movement
    
    if (isClick) {
      onItemToggle(id);
    }
    
    // Don't auto-resume if we're still hovering - let handleMouseLeave handle it
    // This prevents jarring scroll jumps after drag
  };
  
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollContainerRef.current) return;
    e.preventDefault();
    
    // Calculate position changes
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 2; // Scroll speed multiplier
    
    // Calculate and apply the new scroll position
    const newScrollPosition = scrollLeft - walk;
    scrollContainerRef.current.scrollLeft = newScrollPosition;
    
    // Update our tracking references to match the current position
    // This is the critical part for syncing drag position with auto-scroll
    scrollPositionRef.current = scrollContainerRef.current.scrollLeft;
    wasManuallyScrolledRef.current = true;
    lastScrollTimeRef.current = Date.now();
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!scrollContainerRef.current) return;
    
    // Stop any pending resume timer
    if (resumeDelayTimerRef.current) {
      clearTimeout(resumeDelayTimerRef.current);
      resumeDelayTimerRef.current = null;
    }
    
    setIsDragging(true);
    setIsPaused(true);
    setStartX(e.touches[0].pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
    
    // Track for tap detection
    setClickStartTime(Date.now());
    setClickStartPosition({ x: e.touches[0].pageX, y: e.touches[0].pageY });
    
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    
    wasManuallyScrolledRef.current = true;
    transitioningRef.current = true;
  };

  const handleTouchEnd = (e: React.TouchEvent, id: string) => {
    if (!isDragging) return;
    
    setIsDragging(false);
    wasManuallyScrolledRef.current = true;
    
    if (scrollContainerRef.current) {
      scrollPositionRef.current = scrollContainerRef.current.scrollLeft;
    }
    
    // Detect if this was a tap (not a drag)
    const isTap = 
      Date.now() - clickStartTime < 200 && // Short duration
      Math.abs(e.changedTouches[0].pageX - clickStartPosition.x) < 10 && // Small X movement
      Math.abs(e.changedTouches[0].pageY - clickStartPosition.y) < 10;   // Small Y movement
    
    if (isTap) {
      onItemToggle(id);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || !scrollContainerRef.current) return;
    e.preventDefault();
    
    const x = e.touches[0].pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    
    const newScrollPosition = scrollLeft - walk;
    scrollContainerRef.current.scrollLeft = newScrollPosition;
    
    scrollPositionRef.current = scrollContainerRef.current.scrollLeft;
    wasManuallyScrolledRef.current = true;
    lastScrollTimeRef.current = Date.now();
  };

  return (
    <div
      ref={scrollContainerRef}
      className="overflow-hidden flex-grow"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        maskImage: 'linear-gradient(to right, rgba(0, 0, 0, 0) 0%, rgb(0, 0, 0) 5%, rgb(0, 0, 0) 95%, rgba(0, 0, 0, 0) 100%)',
        WebkitMaskImage: 'linear-gradient(to right, rgba(0, 0, 0, 0) 0%, rgb(0, 0, 0) 5%, rgb(0, 0, 0) 95%, rgba(0, 0, 0, 0) 100%)'
      }}
    >
      <div 
        ref={contentRef}
        className="flex flex-nowrap gap-2 cursor-grab"
      >
        {items.map(item => (
          <Badge 
            key={`item-${item.id}`} 
            variant={selectedItems.includes(item.id) ? "default" : "outline"} 
            className={`flex items-center gap-1 px-3 py-1.5 rounded-full whitespace-nowrap transition-all hover:shadow-sm ${
              selectedItems.includes(item.id) 
                ? 'bg-accent text-white hover:bg-accent/90' 
                : 'hover:border-accent hover:text-accent'
            }`} 
            onMouseDown={handleMouseDown}
            onMouseUp={(e) => handleMouseUp(e, item.label)}
            onMouseMove={handleMouseMove}
            onTouchStart={handleTouchStart}
            onTouchEnd={(e) => handleTouchEnd(e, item.label)}
            onTouchMove={handleTouchMove}
          >
            <span className="text-xs">
              {selectedItems.includes(item.id) && <CheckCircle size={12} className="inline-block mr-1" />}
              {item.label}
            </span>
          </Badge>
        ))}
      </div>
    </div>
  );
};

export default InfiniteScrollTags;
